import { SkeletonReveal } from "@albertine/design-system/components/SkeletonReveal";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Lib, useAlbertineTranslation } from "albertine-shared-web";
import { useRef } from "react";
import { openChatUrl } from "@/lib/openChatUrl";
import {
    StickyHeader,
    StickyHeaderBackButton,
} from "@/routes/-components/StickyHeader";
import { useSkeletonRevealPage } from "@/routes/-hooks/useSkeletonRevealPage";
import { ArticleBackButton } from "./-components/ArticleBackButton";
import { ArticleMap } from "./-components/ArticleMap";
import { FooterSection } from "./-components/FooterSection";
import { HeroImages } from "./-components/HeroImages";
import { InsightSection } from "./-components/InsightSection";
import { TagTable } from "./-components/TagTable";
import { TextBlock } from "./-components/TextBlock";
import { TopSection } from "./-components/TopSection";
import {
    ArticleProvider,
    useArticle,
    useIsArticleLoaded,
} from "./-context/ArticleContext";
import { articleSearchSchema } from "./-utils/articleSearchSchema";
import { buildChatUrl } from "./-utils/buildChatUrl";
import { REVEAL_BACKGROUND_CLASS } from "./-utils/revealBackground";

const HERO_BACKGROUND = "var(--components-articles-hero-background)";
const FOOTER_BACKGROUND = "var(--color-background-discover-secondary)";

export const Route = createFileRoute("/article/")({
    validateSearch: articleSearchSchema,
    component: RouteComponent,

    head: ({ match }) => {
        const { tenant, locale } = match.search;
        return {
            meta: [{ title: `Article - ${tenant} ${locale}` }],
        };
    },

    beforeLoad: () => {
        document.documentElement.style.setProperty("--color-body-top", HERO_BACKGROUND);
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            HERO_BACKGROUND,
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const footerRef = useRef<HTMLElement>(null);

    return (
        <ArticleProvider>
            <ArticlePage footerRef={footerRef} />
        </ArticleProvider>
    );
}

type ArticlePageProps = {
    footerRef: React.RefObject<HTMLElement | null>;
};

function ArticlePage({ footerRef }: ArticlePageProps) {
    const article = useArticle();
    const isLoaded = useIsArticleLoaded();
    const { tenant, locale } = Route.useSearch();
    const t = useAlbertineTranslation();
    const chatUrl = buildChatUrl(article, tenant, locale, t);
    const { isRevealed, isVisibleContentRevealed, skeletonRevealProps } =
        useSkeletonRevealPage({
            isLoaded,
            loadingBodyColor: HERO_BACKGROUND,
            revealedBodyColor: FOOTER_BACKGROUND,
        });

    return (
        <>
            <div className="group/article relative" data-revealed={isRevealed}>
                <StickyHeader
                    title={isLoaded ? article.name : ""}
                    variant="article"
                    backButton={<StickyHeaderBackButton />}
                />
                {/* Outside the reveal, which makes its content inert while loading. */}
                <ArticleBackButton />
                <SkeletonReveal {...skeletonRevealProps}>
                    <div className="relative bg-components-articles-hero-background">
                        <TopSection />
                    </div>
                    <HeroImages />
                    <TextBlock />
                    <InsightSection />
                    <ArticleMap />
                    <TagTable />
                    <footer
                        ref={footerRef}
                        className={`bg-background-discover-secondary ${REVEAL_BACKGROUND_CLASS}`}
                    >
                        <FooterSection />
                    </footer>
                </SkeletonReveal>
            </div>

            {/* Waits for the on-screen reveal, so its entrance animation doesn't compete with the blur. */}
            {isVisibleContentRevealed && (
                <Lib.FloatingCTAButton
                    hideTriggerRef={footerRef}
                    insideContentArea={false}
                    opacityOnly
                    hideTriggerOffsetPx={100}
                    background="var(--color-background-discover-quaternary)"
                    onClick={() => {
                        openChatUrl(chatUrl);
                    }}
                >
                    {t("article_footer_button_text")}
                </Lib.FloatingCTAButton>
            )}
        </>
    );
}
